import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import PointsForm from '../../components/HR/PointsForm';
import { AssessmentPoint } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useAssessmentPoints, useCreateAssessmentPoint, useUpdateAssessmentPoint, useDeleteAssessmentPoint } from '../../hooks/useAssessmentPoints';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';

const Points: React.FC = () => {
  const { data: points = [], isLoading, error, refetch } = useAssessmentPoints();
  const createMutation = useCreateAssessmentPoint();
  const updateMutation = useUpdateAssessmentPoint();
  const deleteMutation = useDeleteAssessmentPoint();

  const [showForm, setShowForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState<AssessmentPoint | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [applicableForFilter, setApplicableForFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [weightFilter, setWeightFilter] = useState<string>('all');

  const columns: TableColumn[] = [
    { key: 'name', label: 'Point Name', sortable: true },
    { key: 'category', label: 'Category', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'technical' ? 'bg-blue-100 text-blue-800' : value === 'communication' ? 'bg-green-100 text-green-800' :
        value === 'experience' ? 'bg-purple-100 text-purple-800' : value === 'education' ? 'bg-yellow-100 text-yellow-800' :
        value === 'leadership' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
      }`}>{value.charAt(0).toUpperCase() + value.slice(1)}</span>
    )},
    { key: 'weight', label: 'Weight', sortable: true, render: (value: number) => (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg key={star} className={`h-4 w-4 ${star <= value ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-600">({value})</span>
      </div>
    )},
    { key: 'applicableFor', label: 'Applicable For', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'candidates' ? 'bg-indigo-100 text-indigo-800' : value === 'hiring' ? 'bg-orange-100 text-orange-800' : 'bg-cyan-100 text-cyan-800'
      }`}>{value === 'both' ? 'Both' : value.charAt(0).toUpperCase() + value.slice(1)}</span>
    )},
    { key: 'isActive', label: 'Status', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {value ? 'Active' : 'Inactive'}
      </span>
    )},
    { key: 'description', label: 'Description', render: (value: string) => <div className="max-w-xs truncate" title={value}>{value}</div> }
  ];

  const filteredPoints = useMemo(() => {
    return points.filter(point => {
      const matchesSearch = !searchTerm || (
        point.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        point.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        point.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesCategory = categoryFilter === 'all' || point.category === categoryFilter;
      const matchesApplicableFor = applicableForFilter === 'all' || point.applicableFor === applicableForFilter;
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' && point.isActive) || (statusFilter === 'inactive' && !point.isActive);
      const matchesWeight = weightFilter === 'all' ||
        (weightFilter === '1-2' && point.weight >= 1 && point.weight <= 2) ||
        (weightFilter === '3' && point.weight === 3) ||
        (weightFilter === '4-5' && point.weight >= 4 && point.weight <= 5);
      return matchesSearch && matchesCategory && matchesApplicableFor && matchesStatus && matchesWeight;
    });
  }, [points, searchTerm, categoryFilter, applicableForFilter, statusFilter, weightFilter]);

  const handleExport = () => {
    const exportData = filteredPoints.map(point => ({
      'Point Name': point.name,
      'Category': point.category.charAt(0).toUpperCase() + point.category.slice(1),
      'Weight': point.weight,
      'Applicable For': point.applicableFor === 'both' ? 'Both' : point.applicableFor.charAt(0).toUpperCase() + point.applicableFor.slice(1),
      'Status': point.isActive ? 'Active' : 'Inactive',
      'Description': point.description,
      'Created Date': new Date(point.createdAt).toLocaleDateString(),
      'Updated Date': new Date(point.updatedAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `assessment_points_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredPoints.map(point => ({
      'Point Name': point.name,
      'Category': point.category.charAt(0).toUpperCase() + point.category.slice(1),
      'Weight': point.weight,
      'Applicable For': point.applicableFor === 'both' ? 'Both' : point.applicableFor.charAt(0).toUpperCase() + point.applicableFor.slice(1),
      'Status': point.isActive ? 'Active' : 'Inactive',
      'Description': point.description,
      'Created Date': new Date(point.createdAt).toLocaleDateString(),
      'Updated Date': new Date(point.updatedAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Assessment Points');
  };

  const filters: FilterConfig[] = [
    { key: 'category', label: 'Category', type: 'select', value: categoryFilter, onChange: setCategoryFilter, options: [
      { value: 'all', label: 'All Categories' }, { value: 'technical', label: 'Technical' }, { value: 'communication', label: 'Communication' },
      { value: 'experience', label: 'Experience' }, { value: 'education', label: 'Education' }, { value: 'leadership', label: 'Leadership' }, { value: 'other', label: 'Other' }
    ]},
    { key: 'applicableFor', label: 'Applicable For', type: 'select', value: applicableForFilter, onChange: setApplicableForFilter, options: [
      { value: 'all', label: 'All' }, { value: 'candidates', label: 'Candidates' }, { value: 'hiring', label: 'Hiring' }, { value: 'both', label: 'Both' }
    ]},
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
    ]},
    { key: 'weight', label: 'Weight', type: 'select', value: weightFilter, onChange: setWeightFilter, options: [
      { value: 'all', label: 'All Weights' }, { value: '1-2', label: '1-2 (Low)' }, { value: '3', label: '3 (Average)' }, { value: '4-5', label: '4-5 (High)' }
    ]},
  ];

  const handleAdd = () => { setEditingPoint(undefined); setShowForm(true); };
  const handleEdit = (point: AssessmentPoint) => { setEditingPoint(point); setShowForm(true); };

  const handleDelete = async (point: AssessmentPoint) => {
    if (window.confirm(`Are you sure you want to delete the assessment point "${point.name}"?`)) {
      await deleteMutation.execute(point.documentId || point.id);
    }
  };

  const handleFormSubmit = async (pointData: Partial<AssessmentPoint>) => {
    if (editingPoint) {
      await updateMutation.execute({ id: editingPoint.documentId || editingPoint.id, data: { ...pointData, documentId: editingPoint.documentId } });
    } else {
      await createMutation.execute(pointData);
    }
    setShowForm(false);
    setEditingPoint(undefined);
  };

  const handleFormCancel = () => { setShowForm(false); setEditingPoint(undefined); };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setApplicableForFilter('all');
    setStatusFilter('all');
    setWeightFilter('all');
  };

  const activePoints = filteredPoints.filter(p => p.isActive);
  const candidatePoints = filteredPoints.filter(p => p.applicableFor === 'candidates' || p.applicableFor === 'both');
  const hiringPoints = filteredPoints.filter(p => p.applicableFor === 'hiring' || p.applicableFor === 'both');

  if (isLoading) return <div className="flex justify-center items-center h-64"><div className="text-lg text-gray-500">Loading assessment points...</div></div>;
  if (error) return <ErrorState message="Error loading assessment points. Please try again." onRetry={() => refetch()} entityName="assessment points" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Assessment Points" description="Manage assessment criteria for evaluating candidates and hiring processes." />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Points</dt>
                  <dd className="text-lg font-medium text-gray-900">{points.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Points</dt>
                  <dd className="text-lg font-medium text-gray-900">{activePoints.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 715.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">For Candidates</dt>
                  <dd className="text-lg font-medium text-gray-900">{candidatePoints.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0H8m8 0v6a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0V4a2 2 0 00-2-2H8a2 2 0 00-2 2v2m8 0h3a2 2 0 012 2v6a2 2 0 01-2 2h-3" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">For Hiring</dt>
                  <dd className="text-lg font-medium text-gray-900">{hiringPoints.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!showForm && (
        <>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search points..."
            filters={filters}
            onClearFilters={handleClearFilters}
            totalCount={points.length}
            filteredCount={filteredPoints.length}
            entityName="assessment points"
            onAdd={handleAdd}
            addLabel="Add Point"
            onExport={handleExport}
            onPrint={handlePrint}
          />
          <div className="card">
            <Table data={filteredPoints} columns={columns} onEdit={handleEdit} onDelete={handleDelete} showAddButton={false} searchable={false} />
          </div>
        </>
      )}

      {showForm && (
        <PointsForm point={editingPoint} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      )}
    </div>
  );
};

export default Points;
